import React, { useState, useEffect } from 'react';
import TransferService from '../../services/transferService';

const DEFAULT_WINDOW_DURATION_MS = 30 * 24 * 60 * 60 * 1000;
const MANUAL_WINDOW_NAME = 'Manual';
const canCloseLeagueWindow = (windowStatus) => (
  windowStatus?.status === 'open' && windowStatus?.window?.league != null
);

const TransferWindowToggle = ({ leagueId, leagueSeason, isLeagueOwner }) => {
  const [windowStatus, setWindowStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!isLeagueOwner || !leagueId) {
      setWindowStatus(null);
      setLoading(false);
      return;
    }

    fetchWindowStatus();
  }, [leagueId, isLeagueOwner]);

  const fetchWindowStatus = async () => {
    if (!isLeagueOwner || !leagueId) {
      setWindowStatus(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await TransferService.getCurrentTransferWindowInfo(leagueId);
      setWindowStatus(response);
    } catch (error) {
      console.error('Error fetching transfer window status:', error);
      setWindowStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWindow = async (action) => {
    if (!leagueId || actionLoading) return;

    if (action === 'close' && !canCloseLeagueWindow(windowStatus)) {
      return;
    }

    try {
      setActionLoading(true);

      if (action === 'open') {
        const currentDate = new Date();
        await TransferService.createOrUpdateTransferWindow({
          leagueId,
          // Keep scheduled Summer/Winter windows intact when the owner opens the market early.
          name: MANUAL_WINDOW_NAME,
          season: leagueSeason,
          startDate: currentDate,
          endDate: new Date(currentDate.getTime() + DEFAULT_WINDOW_DURATION_MS),
        });
      } else {
        await TransferService.closeTransferWindow({
          leagueId,
          windowType: windowStatus?.window?.name,
          season: windowStatus?.window?.season ?? leagueSeason,
        });
      }

      await fetchWindowStatus();
    } catch (error) {
      console.error('Error toggling transfer window:', error);
      alert('Error updating transfer window: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (!isLeagueOwner || !leagueId) {
    return null;
  }

  if (loading) {
    return (
      <div className="league-settings-card transfer-window-card">
        <div className="card-header">
          <h3>Transfer Window</h3>
        </div>
        <div className="card-content">
          <p style={{ textAlign: 'center', padding: '20px 0', color: '#B0B0B0' }}>Loading...</p>
        </div>
      </div>
    );
  }

  const isWindowOpen = windowStatus?.status === 'open';
  // Legacy fallback windows are shared rows with no league scope, so the close API cannot close them for one league.
  const canCloseCurrentWindow = canCloseLeagueWindow(windowStatus);

  return (
    <div className="league-settings-card transfer-window-card">
      <div className="card-header">
        <h3>Transfer Window</h3>
        <span className={`card-badge ${isWindowOpen ? 'badge-open' : 'badge-closed'}`}>
          {isWindowOpen ? 'OPEN' : 'CLOSED'}
        </span>
      </div>
      <div className="card-content">
        <div className="transfer-info">
          {isWindowOpen && windowStatus?.window ? (
            <p>Window closes: <strong>{new Date(windowStatus.window.endDate).toLocaleDateString()}</strong></p>
          ) : windowStatus?.nextWindow ? (
            <p>Next scheduled window: <strong>{windowStatus.nextWindow.name}</strong></p>
          ) : (
            <p>Transfer window is currently closed. Open it to allow player transfers in this league.</p>
          )}
        </div>
        <div className="transfer-actions">
          <button
            className="btn-open"
            onClick={() => handleToggleWindow('open')}
            disabled={actionLoading || isWindowOpen}
          >
            {actionLoading && !isWindowOpen ? 'Opening...' : 'Open Window'}
          </button>
          {(!isWindowOpen || canCloseCurrentWindow) && (
            <button
              className="btn-close"
              onClick={() => handleToggleWindow('close')}
              disabled={actionLoading || !isWindowOpen}
            >
              {actionLoading && isWindowOpen ? 'Closing...' : 'Close Window'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransferWindowToggle;
